import {SubmitState} from "../../components-shared/forms/form-submit-button/form-submit-button"
import {ProblemDetails} from "./models/problem-details"

export class UserReadableError extends Error {}
export class TechnicalError extends Error {}

export const throwOnErrorWithMessage = async (response: Response) => {
  if (response.ok) return

  const throwResponseError = () => {
    // Throw a generic response based on the response status. Technical errors are shown alongside a user-readable generic message.
    throw new TechnicalError(`Error with status ${response.status} occurred: ${response.statusText}`)
  }

  // 404 error messages can be communicated to the user
  if (response.status === 400) {
    let problemDetails = {title: ""}
    try {
      problemDetails = await response.json() as ProblemDetails
    } catch (err) {
      // apparently this is no problemDetails object.
      throwResponseError()
    }

    if (!problemDetails.title) throwResponseError()
    else throw new UserReadableError(problemDetails.title)
  }

  throwResponseError()
}

export const performWithSubmitStateAndApiCallErrorHandling = async (apiCallAction: () => Promise<any>, baseErrorMessage: string, setErrorMessage: (errorMessage?: string) => void, setSubmitState: (submitState: SubmitState) => void, performAfterSuccess?: () => void) => {
  await performWithApiCallErrorHandling(
    () => {
      setSubmitState(SubmitState.Submitting)
      const response = apiCallAction()
      setSubmitState(SubmitState.Submitted)
      return response
    },
    baseErrorMessage,
    setErrorMessage,
    performAfterSuccess,
    () => {
      setSubmitState(SubmitState.Initial)
    })
}

export const performWithApiCallErrorHandling = async (apiCallAction: () => Promise<any>, baseErrorMessage?: string, setErrorMessage?: (errorMessage?: string) => void, performAfterSuccess?: (result: any) => void, performAfterFailure?: () => void): Promise<any> => {
  const apiCallErrorHandler = getApiCallErrorMessageHandler(baseErrorMessage ?? "", setErrorMessage ?? (() => {
  }))
  if (setErrorMessage !== undefined) setErrorMessage(undefined)
  return await apiCallAction()
    .then((result) => {
      if (performAfterSuccess !== undefined) performAfterSuccess(result)
      return result
    })
    .catch((err) => {
      apiCallErrorHandler(err)
      if (performAfterFailure !== undefined) performAfterFailure()
    })
}

const DefaultErrorMessage = "An error occurred"

export const getApiCallErrorMessageHandler = (baseMessage?: string, setErrorMessage?: (errorMessage?: string) => void | undefined) =>
  (err: Error) => {
    if (err?.name === "AbortError") {
      console.log("Fetch aborted")
    } else {
      console.error("An error occurred", err)
      if (setErrorMessage) {
        if (err instanceof UserReadableError) {
          setErrorMessage(err.message)
        } else if (err instanceof TechnicalError) {
          setErrorMessage(`${baseMessage ?? DefaultErrorMessage} ${err.message}`)
        } else {
          setErrorMessage(`${baseMessage ?? DefaultErrorMessage}`)
        }
      }
    }
  }